import React, { useState, useMemo, useCallback, useRef } from 'react';
import { reduce, map, filter, keys, noop, compose, partial } from 'underscore'; // eslint-disable-line max-len
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Modal,
  Stack,
  IconButton,
  Text,
  ChoiceGroup,
  Link,
  TextField,
  Checkbox,
  PrimaryButton,
  DefaultButton,
} from '@fluentui/react';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { Glyphicon } from '@bingads-webui-react/primitive';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  surveyStyles,
  iconButtonStyles,
} from './styles';

const surveyMaxWidth = 480;
const commentMaxLength = 2000;

function SurveyViewInternal(props) {
  const {
    classes,
    i18n,
    serverContext,
    surveyDataService,
    onSurveySubmit,
    onSurveyDismiss,
  } = props;

  const surveyRef = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState(true);

  const [selectedOption, setSelectedOption] = useState(null);

  const [validationError, setValidationError] = useState(true);

  const validate = useCallback((option) => {
    if (option) {
      setValidationError('');
    } else {
      setValidationError(i18n.getString(_TL_('Please answer this question')));
    }
  }, [i18n]);

  const onChangeRating = useCallback((_, option) => {
    setSelectedOption(option);
    validate(option);
  }, [validate]);

  const ratingOptions = useMemo(() => {
    const options = [
      {
        key: 'very-satisfied',
        text: i18n.getString(_TL_('Very satisfied')),
        rating: 1.0,
        isAdditionalContentVisible: false,
      },
      {
        key: 'moderately-satisfied',
        text: i18n.getString(_TL_('Moderately satisfied')),
        rating: 0.8,
        isAdditionalContentVisible: false,
      },
      {
        key: 'neither-satisfied-nor-dissatisfied',
        text: i18n.getString(_TL_('Neither satisfied or dissatisfied')),
        rating: 0.6,
        isAdditionalContentVisible: true,
      },
      {
        key: 'moderately-dissatisfied',
        text: i18n.getString(_TL_('Moderately dissatisfied')),
        rating: 0.4,
        isAdditionalContentVisible: true,
      },
      {
        key: 'very-dissatisfied',
        text: i18n.getString(_TL_('Very dissatisfied')),
        rating: 0.2,
        isAdditionalContentVisible: true,
      },
    ];

    return options.map(value => ({
      ariaLabel: value.text,
      onRenderField: (_props, render) => (
        <div className={classNames(classes.ratingOption, 'survey-rating-option', value.key)}>
          {render(_props)}
        </div>
      ),
      ...value,
    }));
  }, [i18n, classes.ratingOption]);

  const areaOptions = useMemo(() => [
    {
      key: 'campaignCreationAndEditing',
      label: i18n.getString(_TL_('Campaign creation and editing')),
      withComment: false,
    }, {
      key: 'interfaceNavigation',
      label: i18n.getString(_TL_('Interface navigation')),
      withComment: false,
    }, {
      key: 'searchAndFiltering',
      label: i18n.getString(_TL_('Search and filtering')),
      withComment: false,
    }, {
      key: 'theOverviewPage',
      label: i18n.getString(_TL_('The overview page')),
      withComment: false,
    }, {
      key: 'others',
      label: i18n.getString(_TL_('Others - Please feel free to elaborate below')),
      withComment: true,
    },
  ], [i18n]);

  const initialAreaState = useMemo(() => reduce(areaOptions, (memo, { key }) => ({
    ...memo,
    [key]: false,
  }), {}), [areaOptions]);

  const [areaState, setAreaState] = useState(initialAreaState);

  const [areaComment, setAreaComment] = useState('');
  const [areaCommentDescription, setAreaCommentDescription] = useState('');
  const [areaCommentError, setAreaCommentError] = useState('');

  const onChangeAreaComment = useCallback((_, val) => {
    setAreaComment(val);
    if (val.length > commentMaxLength) {
      setAreaCommentDescription('');
      setAreaCommentError('Field should not exceed 2000 characters');
    } else {
      setAreaCommentDescription(`${commentMaxLength - val.length} characters remaining`);
      setAreaCommentError('');
    }
  }, []);

  const [comment, setComment] = useState('');
  const [commentDescription, setCommentDescription] = useState('');
  const [commentError, setCommentError] = useState('');

  const onChangeComment = useCallback((_, val) => {
    setComment(val);
    if (val.length > commentMaxLength) {
      setCommentDescription('');
      setCommentError('Field should not exceed 2000 characters');
    } else {
      setCommentDescription(`${commentMaxLength - val.length} characters remaining`);
      setCommentError('');
    }
  }, []);

  const [emailOption, setEmailOption] = useState(false);
  const [email, setEmail] = useState(serverContext.CurrentUser.Email);
  const [emailError, setEmailError] = useState('');

  const onChangeEmailOption = useCallback((_, option) => {
    setEmailOption(option);
  }, []);

  const validateEmailAddress = useCallback((emailAddress) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(emailAddress);
  }, []);

  const onChangeEmail = useCallback((_, val) => {
    setEmail(val);
    if (validateEmailAddress(val)) {
      setEmailError('');
    } else {
      setEmailError(i18n.getString(_TL_('Invalid email address')));
    }
  }, [validateEmailAddress, i18n]);

  const emailOptions = useMemo(() => {
    const options = [
      {
        key: 'yes',
        text: i18n.getString(_TL_('Yes - specify the contact email below')),
      },
      {
        key: 'no',
        text: i18n.getString(_TL_('No')),
      },
    ];

    return options.map(value => ({
      ariaLabel: value.text,
      onRenderField: (_props, render) => (
        <div className={classNames(classes.emailOption, 'survey-email-option', value.key)}>
          {render(_props)}
          {emailOption && emailOption.key === 'yes' && value.key === 'yes' &&
            <React.Fragment>
              <TextField
                className={classNames(classes.emailInput, 'survey-email-input')}
                ariaLabel={i18n.getString(_TL_('Email address'))}
                placeholder={i18n.getString(_TL_('Enter your email'))}
                value={email}
                onChange={onChangeEmail}
              />
              <div className={classNames(classes.emailError, 'survey-email-error')}>
                {emailError}
              </div>
            </React.Fragment>
          }
        </div>
      ),
      ...value,
    }));
  }, [i18n, classes.emailOption, emailOption, classes.emailInput, email, onChangeEmail, classes.emailError, emailError]);

  const [disableSubmitButton, setDisableSubmitButton] = useState(false);

  const onSubmit = useCallback(() => {
    validate(selectedOption);

    if (validationError) {
      return;
    }

    const selectedAreas = filter(keys(areaState), key => areaState[key] === true);
    const additionalAreas = areaState.others ? `,${areaComment}` : '';
    const feedback = {
      comment: `Areas:${selectedAreas.toString()}${additionalAreas} | Comment:${comment}`,
      rating: selectedOption.rating,
    };
    if (emailOption && emailOption.key === 'yes') {
      feedback.email = email;
    }
    const telemetry = {
      featureArea: 'WebUIUserCSAT',
    };
    surveyDataService.sendFeedback({
      feedback,
      telemetry,
      onFinishSendRequest: () => {
        setIsModalOpen(false);
        onSurveySubmit();
      },
    });

    setDisableSubmitButton(true);
  }, [validate, validationError, selectedOption, areaState, areaComment, comment, emailOption, email, surveyDataService, onSurveySubmit]);

  const onDismiss = useCallback(() => {
    setIsModalOpen(false);
    onSurveyDismiss();
  }, [onSurveyDismiss]);

  const cancelIcon = useMemo(() => ({
    iconName: 'Cancel',
  }), []);

  return (
    <Modal
      className={classNames('webui-user-survey')}
      isOpen={isModalOpen}
      onDismiss={onDismiss}
      isBlocking={false}
      isModeless
      styles={{
        main: {
          position: 'absolute',
          right: '40px',
          bottom: '0',
          zIndex: 10000000,
        },
      }}
    >
      <div ref={surveyRef} className={classNames(classes.container, 'survey-container')}>
        <Stack>
          <div className={classNames(classes.header, 'survey-header')}>
            <Text className={classNames(classes.title, 'survey-title')}>
              {i18n.getString(_TL_('Feedback to Microsoft'))}
            </Text>
            <IconButton
              className={classNames(classes.closeButton, 'survey-close-button')}
              styles={iconButtonStyles}
              iconProps={cancelIcon}
              ariaLabel={i18n.getString(_TL_('Close survey'))}
              onClick={onDismiss}
            />
          </div>
          <div className={classNames(classes.body, 'survey-body')}>
            <Text className={classNames(classes.ratingDescription, 'survey-rating-description')}>
              {i18n.getString(_TL_('Please rate your overall satisfaction with Microsoft Advertising\'s interface. '))}
              <span className="asterisk">*</span>
            </Text>
            <div className={classNames(classes.validationError, 'survey-validation-error')}>
              {validationError}
            </div>
            <ChoiceGroup
              className={classNames(classes.rating, 'survey-rating')}
              options={ratingOptions}
              onChange={onChangeRating}
              selectedKey={selectedOption ? selectedOption.key : null}
              ariaLabel={i18n.getString(_TL_('Overall satisfaction'))}
            />
            {selectedOption && selectedOption.isAdditionalContentVisible &&
              <React.Fragment>
                <div className={classNames(classes.area, 'survey-area')}>
                  <div className={classNames(classes.areaDescription, 'survey-area-description')}>
                    <ReactTemplate
                      model={{
                        b: () => (`${selectedOption.text}`),
                      }}
                      template={
                        i18n.getString(_TL_('Can you tell us more about where you are {{b}}{{/b}} with our interface?'))
                      }
                    />
                  </div>
                  {map(areaOptions, ({ key, label, withComment }) => {
                    const checkbox =
                      (<Checkbox
                        className={classNames(classes.areaCheckbox, 'survey-area-checkbox', key)}
                        checked={areaState[key]}
                        label={label}
                        key={key}
                        onChange={(_, checked) => {
                          setAreaState(prev => ({
                            ...prev,
                            [key]: checked,
                          }));
                        }}
                      />);
                    if (withComment && areaState[key]) {
                      return (
                        <React.Fragment>
                          {checkbox}
                          <TextField
                            className={classNames(classes.areaComment, 'survey-area-comment')}
                            ariaLabel={i18n.getString(_TL_('Other areas'))}
                            placeholder={i18n.getString(_TL_('Please feel free to elaborate'))}
                            value={areaComment}
                            onChange={onChangeAreaComment}
                            description={areaCommentDescription}
                            errorMessage={areaCommentError}
                          />
                        </React.Fragment>
                      );
                    }
                    return checkbox;
                  })}
                </div>
                <div className={classNames(classes.comment, 'survey-comment')}>
                  <Text className={classNames(classes.commentDescription, 'survey-comment-description')}>
                    {i18n.getString(_TL_('How can we continue to improve?'))}
                  </Text>
                  <TextField
                    className={classNames(classes.commentInput, 'survey-comment-input')}
                    ariaLabel={i18n.getString(_TL_('How can we continue to improve?'))}
                    multiline
                    rows={3}
                    resizable={false}
                    placeholder={i18n.getString(_TL_('Please do not include any confidential or personal information in your feedback.'))}
                    value={comment}
                    onChange={onChangeComment}
                    description={commentDescription}
                    errorMessage={commentError}
                  />
                </div>
                <div className={classNames(classes.email, 'survey-email')}>
                  <Text className={classNames(classes.emailDescription, 'survey-email-description')}>
                    {i18n.getString(_TL_('Can we follow up with you about yor responses?'))}
                  </Text>
                  <ChoiceGroup
                    className={classNames(classes.emailOptions, 'survey-email-options')}
                    options={emailOptions}
                    onChange={onChangeEmailOption}
                    selectedKey={emailOption ? emailOption.key : null}
                    ariaLabel={i18n.getString(_TL_('Email option'))}
                  />
                </div>
              </React.Fragment>
            }
          </div>
          <div className={classNames(classes.footer, 'survey-footer')}>
            <div className={classNames(classes.privacyStatement, 'survey-privacy-statement')}>
              <ReactTemplate
                model={{
                  a: text => (
                    <Link
                      href="https://go.microsoft.com/fwlink/?LinkId=521839"
                      className={classNames(classes.infoText, 'survey-info-text')}
                      target="_blank"
                    >
                      {`${text} `}
                      <Glyphicon glyph="ba-OpenInNewWindow" />
                    </Link>
                  ),
                }}
                template={
                  i18n.getString(_TL_('By pressing submit, your feedback will be used to improve Microsoft products and services. {{a}}Privacy statement{{/a}}'))
                }
              />
            </div>
            <div className={classNames(classes.buttonBar, 'survey-button-bar')}>
              <PrimaryButton
                className={classNames(classes.submitButton, 'submit')}
                text={i18n.getString(_TL_('Submit'))}
                onClick={onSubmit}
                disabled={disableSubmitButton}
              />
              <DefaultButton
                className={classNames(classes.cancelButton, 'cancel')}
                text={i18n.getString(_TL_('Cancel'))}
                onClick={onDismiss}
              />
            </div>
          </div>
        </Stack>
      </div>
    </Modal>
  );
}

SurveyViewInternal.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentUser: PropTypes.shape({
      Email: PropTypes.string.isRequired,
    }),
  }).isRequired,
  classes: PropTypes.shape({
    container: PropTypes.string,
    header: PropTypes.string,
    title: PropTypes.string,
    body: PropTypes.string,
    rating: PropTypes.string,
    ratingDescription: PropTypes.string,
    ratingOption: PropTypes.string,
    area: PropTypes.string,
    areaDescription: PropTypes.string,
    areaCheckbox: PropTypes.string,
    areaComment: PropTypes.string,
    comment: PropTypes.string,
    commentDescription: PropTypes.string,
    commentInput: PropTypes.string,
    email: PropTypes.string,
    emailDescription: PropTypes.string,
    emailOptions: PropTypes.string,
    emailOption: PropTypes.string,
    emailInput: PropTypes.string,
    footer: PropTypes.string,
    privacyStatement: PropTypes.string,
    infoText: PropTypes.string,
    validationError: PropTypes.string,
    emailError: PropTypes.string,
    buttonBar: PropTypes.string,
    submitButton: PropTypes.string,
    cancelButton: PropTypes.string,
    closeButton: PropTypes.string,
  }).isRequired,
  onSurveySubmit: PropTypes.func,
  onSurveyDismiss: PropTypes.func,
  surveyDataService: PropTypes.shape({
    sendFeedback: PropTypes.func.isRequired,
  }).isRequired,
};

SurveyViewInternal.defaultProps = {
  onSurveySubmit: noop,
  onSurveyDismiss: noop,
};

export const SurveyView = compose(
  WrappedComponent => withDefaultStyles(WrappedComponent, partial(surveyStyles, surveyMaxWidth)),
  withI18nConsumer,
  withServerContext
)(SurveyViewInternal);
